// Adaptive routing / RouteLLM contracts (doc 45). Adaptive routing runs AFTER
// identity/entitlement/allowlist/policy/capability/data-classification gates and
// BEFORE quota reservation + provider execution. It can never widen policy.

import type { VelugaRouteHints } from './agent-ir.js';

export type AdaptiveTaskKind =
  | 'draft'
  | 'summary'
  | 'classification'
  | 'query_rewrite'
  | 'planning'
  | 'compliance'
  | 'approval'
  | 'final_answer'
  | 'legal'
  | 'financial'
  | 'security';

export type AdaptiveMode = 'offline-evaluator' | 'shadow' | 'runtime';

export type AllowedAdaptiveTaskKind =
  | 'draft'
  | 'summary'
  | 'classification'
  | 'query_rewrite'
  | 'planning';

export const DEFAULT_ALLOWED_TASK_KINDS: readonly AllowedAdaptiveTaskKind[] = [
  'draft', 'summary', 'classification', 'query_rewrite', 'planning',
];
export const DENIED_TASK_KINDS = [
  'compliance', 'approval', 'final_answer', 'legal', 'financial', 'security',
] as const;
export const MEDIUM_BAND_RATIO = 0.55;

const ADAPTIVE_TASK_KINDS = new Set<string>([...DEFAULT_ALLOWED_TASK_KINDS, ...DENIED_TASK_KINDS]);
const RISK_LEVELS = new Set(['low', 'medium', 'high']);
const DATA_CLASSIFICATIONS = new Set(['public', 'internal', 'confidential', 'secret']);
const ROUTE_MODES = new Set([
  'fixed', 'strong-only', 'local-only', 'cheap-first-with-escalation',
  'adaptive-cost', 'adaptive-quality',
]);

export function normalizeRouteHints(raw: unknown): VelugaRouteHints | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const value = raw as Record<string, unknown>;
  const hints: VelugaRouteHints = {};
  if (typeof value.taskKind === 'string' && ADAPTIVE_TASK_KINDS.has(value.taskKind)) hints.taskKind = value.taskKind as AdaptiveTaskKind;
  if (typeof value.riskLevel === 'string') {
    hints.riskLevel = RISK_LEVELS.has(value.riskLevel) ? value.riskLevel as VelugaRouteHints['riskLevel'] : 'high';
  }
  if (typeof value.difficulty === 'number' && Number.isFinite(value.difficulty) && value.difficulty >= 0 && value.difficulty <= 1) {
    hints.difficulty = value.difficulty;
  }
  if (typeof value.dataClassification === 'string' && DATA_CLASSIFICATIONS.has(value.dataClassification)) hints.dataClassification = value.dataClassification as VelugaRouteHints['dataClassification'];
  if (value.finalAnswer) hints.finalAnswer = true;
  if (value.complianceSensitive) hints.complianceSensitive = true;
  if (typeof value.mode === 'string' && ROUTE_MODES.has(value.mode)) {
    hints.mode = value.mode as VelugaRouteHints['mode'];
  }
  return Object.keys(hints).length ? hints : undefined;
}

export function resolveAutoRoutingEffective(auto?: {
  objective?: 'balanced' | 'cost' | 'quality';
  thresholdOverride?: number;
  allowedTaskKinds?: AllowedAdaptiveTaskKind[];
}): {
  threshold: number;
  thresholdSource: 'objective' | 'override';
  mediumBandThreshold: number;
  allowedTaskKinds: AllowedAdaptiveTaskKind[];
} {
  const objectiveThreshold =
    auto?.objective === 'quality' ? 0.35 : auto?.objective === 'cost' ? 0.75 : 0.55;
  const threshold = auto?.thresholdOverride ?? objectiveThreshold;
  return {
    threshold,
    thresholdSource: auto?.thresholdOverride === undefined ? 'objective' : 'override',
    mediumBandThreshold: threshold * MEDIUM_BAND_RATIO,
    allowedTaskKinds: auto?.allowedTaskKinds
      ? [...auto.allowedTaskKinds]
      : [...DEFAULT_ALLOWED_TASK_KINDS],
  };
}

const PURE_GREETINGS = new Set([
  'hi',
  'hello',
  'hey',
  'good morning',
  'good afternoon',
  'good evening',
  'thanks',
  'thank you',
  'bye',
  'goodbye',
  '안녕',
  '안녕하세요',
  '반가워',
  '반갑습니다',
  '감사합니다',
  '고마워',
  '고맙습니다',
]);

/** Exact, punctuation/emoji-tolerant greeting detector shared by preview and execution. */
export function isPureGreetingText(text: string): boolean {
  const normalized = text
    .toLowerCase()
    .replace(/\p{Extended_Pictographic}/gu, ' ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
  return PURE_GREETINGS.has(normalized);
}

export interface AdaptiveRoutingConfig {
  id: string;
  tenantId: string;
  enabled: boolean;
  mode: AdaptiveMode;
  router: 'routellm' | 'veluga-rule' | 'veluga-learned';
  allowedTaskKinds: AllowedAdaptiveTaskKind[];
  deniedTaskKinds: Array<(typeof DENIED_TASK_KINDS)[number]>;
  thresholds: Record<string, number>;
  maxWeakModelFailureRate: number;
  maxCitationFailureRate: number;
  maxSchemaFailureRate: number;
  closedNetworkMode: 'disabled' | 'local-only' | 'approved-on-prem-only';
}

export interface RoutingEvaluationReport {
  id: string;
  tenantId: string;
  sampleWindow: { start: string; end: string };
  sampleCount: number;
  strongCallPct: number;
  estimatedCostReductionPct: number;
  schemaFailureDeltaPct: number;
  citationFailureDeltaPct: number;
  approvalEscalationDeltaPct: number;
  userRetryDeltaPct: number;
  recommendedMode: 'disabled' | 'shadow' | 'runtime-low-risk-only';
  createdAt: string;
}
